import React, { useState, useEffect, useRef } from 'react';
import AppMockup from './AppMockup';
import DesktopElements from './DesktopElements';
import { trackGetStartedClick, setHomepageVariant } from '../utils/analytics';
import { isPrerendering } from '../utils/prerendering';
import ScheduleDemoModal from './ScheduleDemoModal';
import './Hero.css';

const Hero = () => {
  const [showScheduleDemoModal, setShowScheduleDemoModal] = useState(false);
  const heroRef = useRef(null);
  const backgroundRef = useRef(null);
  const typingIntervalRef = useRef(null);

  // Redirect to testmindfrontend (app.qate.ai) for signup
  const saasUrl = process.env.REACT_APP_SAAS_URL || 'https://app.qate.ai';

  // Set variant when directly visiting /original
  useEffect(() => {
    if (window.location.pathname === '/original') {
      setHomepageVariant('original');
    }
  }, []);

  const handleCTAClick = (e) => {
    e.preventDefault();
    trackGetStartedClick('hero-original');
    window.gtag_report_conversion(`${saasUrl}/get-started`);
  };

  const handleScheduleDemoClick = (e) => {
    e.preventDefault();
    setShowScheduleDemoModal(true);
  };

  // Parallax effect for background elements - disabled on mobile to prevent crashes
  useEffect(() => {
    if (isPrerendering) return;
    // Skip parallax on mobile devices (768px or less)
    const isMobile = window.innerWidth <= 768;
    if (isMobile) return;

    let ticking = false;
    // Cache the elements once instead of querying on every scroll
    const backgroundElements = backgroundRef.current?.querySelectorAll('.parallax-element');
    if (!backgroundElements || backgroundElements.length === 0) return;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrolled = window.pageYOffset;
          backgroundElements.forEach((element, index) => {
            const speed = 0.5 + (index * 0.1);
            const yPos = -(scrolled * speed);
            element.style.transform = `translateY(${yPos}px)`;
          });
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fixed typing animation effect with cycling text
  useEffect(() => {
    // Skip during pre-rendering so captured HTML matches React's initial render
    if (isPrerendering) return;

    const textElement = heroRef.current?.querySelector('.typing-text');
    if (!textElement) {
      console.warn('Typing text element not found');
      return;
    }

    const texts = ['uses your app like a real user', 'tests through the UI'];
    let currentTextIndex = 0;
    let currentCharIndex = 0;
    let isDeleting = false;
    let isWaiting = false;

    // Initialize the element
    textElement.textContent = '';
    textElement.classList.add('active');
    
    // Clear any existing intervals
    if (typingIntervalRef.current) {
      clearTimeout(typingIntervalRef.current);
    }

    const typeWriter = () => {
      if (!textElement) return; // Safety check
      
      if (isWaiting) {
        typingIntervalRef.current = setTimeout(() => {
          isWaiting = false;
          isDeleting = true;
          typeWriter();
        }, 4000); // Wait 4 seconds before starting to delete
        return;
      }

      const currentText = texts[currentTextIndex];
      
      if (!isDeleting) {
        // Typing
        if (currentCharIndex < currentText.length) {
          textElement.textContent = currentText.substring(0, currentCharIndex + 1);
          currentCharIndex++;
          typingIntervalRef.current = setTimeout(typeWriter, 80); // Typing speed
        } else {
          // Finished typing, wait before deleting
          isWaiting = true;
          typeWriter();
        }
      } else {
        // Deleting
        if (currentCharIndex > 0) {
          textElement.textContent = currentText.substring(0, currentCharIndex - 1);
          currentCharIndex--;
          typingIntervalRef.current = setTimeout(typeWriter, 50); // Deleting speed (faster)
        } else {
          // Finished deleting, move to next text
          isDeleting = false;
          currentTextIndex = (currentTextIndex + 1) % texts.length;
          typingIntervalRef.current = setTimeout(typeWriter, 500); // Brief pause before typing next text
        }
      }
    };

    // Start typing after initial delay to ensure DOM is ready
    typingIntervalRef.current = setTimeout(() => {
      console.log('Starting typing animation');
      typeWriter();
    }, 1000);

    // Cleanup function to prevent memory leaks
    return () => {
      if (typingIntervalRef.current) {
        clearTimeout(typingIntervalRef.current);
      }
    };
  }, []); // Empty dependency array ensures this runs once on mount

  return (
    <section className="hero" ref={heroRef}>
      {/* Enhanced background with your color palette */}
      <div className="hero-background" ref={backgroundRef}>
        <div className="parallax-element bg-gradient-1"></div>
        <div className="parallax-element bg-gradient-2"></div>
        <div className="parallax-element bg-gradient-3"></div>
        
        {/* Floating geometric shapes */}
        <div className="floating-shapes">
          <div className="shape-circle shape-1"></div>
          <div className="shape-square shape-2"></div>
          <div className="shape-triangle shape-3"></div>
          <div className="shape-hexagon shape-4"></div>
        </div>
        
        {/* Grid pattern overlay */}
        <div className="grid-pattern"></div>
        
        {/* Animated light rays */}
        <div className="light-rays">
          <div className="ray ray-1"></div>
          <div className="ray ray-2"></div>
          <div className="ray ray-3"></div>
        </div>
      </div>
      
      <DesktopElements />
      
      <div className="hero-container">
        <div className="hero-content">
          <div className="hero-callout">
            <span className="hero-callout-text">
              AI that interacts with your UI
              <svg className="hero-callout-lock" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
            </span>
            <svg className="hero-callout-arrow" viewBox="0 0 60 40" fill="none">
              <path d="M30 5 Q30 20 25 30 Q22 35 18 37" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
              <path d="M22 40 L17 36 L15 30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
          </div>
          <h1>
            <span className="headline-secondary">
              GenAI Quality Assurance
            </span>
            <span className="headline-dynamic">
              that <span className="typing-text" data-typing="true" suppressHydrationWarning></span>
            </span>
          </h1>
          
          <p className="hero-description">
            <strong className="cycle-flow">AI that clicks, types, and navigates</strong> just like a real user.
            Watch it explore your application, create comprehensive tests,
            and deliver <strong className="highlight-feature">automated bugfixes</strong> when things break.
          </p>
          
          <div className="hero-action-card">
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-checkmark">✓</div>
                <span className="stat-label">AI clicks, types, and navigates your app</span>
              </div>
              <div className="stat-item">
                <div className="stat-checkmark">✓</div>
                <span className="stat-label">Self-healing tests + <span className="animated-highlight">automated bug fixing</span></span>
              </div>
              <div className="stat-item">
                <div className="stat-checkmark">✓</div>
                <span className="stat-label">Web + <span className="animated-highlight-blue">Windows desktop</span> + API testing</span>
              </div>
            </div>

            <div className="hero-cta-section">
              <div className="hero-buttons">
                <button className="hero-cta-button hero-cta-primary" onClick={handleCTAClick}>
                  <span className="hero-btn-content">
                    <span className="hero-btn-label">Get Started Free</span>
                    <span className="hero-btn-sub">No credit card · Setup in 60s</span>
                  </span>
                  <span className="hero-btn-arrow">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14" />
                      <path d="m12 5 7 7-7 7" />
                    </svg>
                  </span>
                </button>
                <button className="hero-cta-button hero-cta-secondary" onClick={handleScheduleDemoClick}>
                  <span className="hero-btn-icon-secondary">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                  </span>
                  <span className="hero-btn-content">
                    <span className="hero-btn-label-secondary">Schedule Demo</span>
                    <span className="hero-btn-sub-secondary">15 min · Live walkthrough</span>
                  </span>
                </button>
              </div>
              <p className="hero-cta-note">No credit card required · Free forever · Create with us, run anywhere</p>
            </div>
          </div>
        </div>
        
        <div className="hero-visual">
          <div className="visual-container">
            <div className="visual-glow"></div>
            <AppMockup />
          </div>
        </div>
      </div>
      
      {/* Trust marquee strip */}
      <div className="hero-trust-strip">
        <div className="trust-marquee">
          <div className="trust-track">
            {[...Array(4)].flatMap(() => ['Web Testing', 'Desktop Apps', 'API Testing', 'Self-Healing', 'Auto Bug Fix', 'CI/CD Ready', 'Cross-Browser', 'Visual Regression']).map((item, i) => (
              <span className="trust-item" key={i}>
                <span className="trust-diamond">&#9670;</span>
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="scroll-indicator">
        <div className="scroll-arrow"></div>
        <span className="scroll-text">Discover More</span>
      </div>

      {showScheduleDemoModal && <ScheduleDemoModal onClose={() => setShowScheduleDemoModal(false)} />}
    </section>
  );
};

export default Hero;
